import { createSignal, createEffect, createMemo, onCleanup, onMount, Show, For } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { createStore, reconcile } from "solid-js/store";
import { useApi, isOk } from "../api";
import Text from "../components/Text";
import { useTranslation } from "../hooks/useTranslation";
import OnlinePlayersList from "../components/OnlinePlayersList";
import { subscribeDomainStream, type DomainSnapshot, type DomainSummary } from "../services/onlinePlayersApi";
import styles from "./OnlinePlayers.module.css";

const DOMAINS_POLL_MS = 10_000;
const SELECTED_DOMAIN_KEY = "vektor.onlinePlayers.domain";

function readSelectedDomain(): string {
  if (typeof localStorage === "undefined") return "";
  try {
    return localStorage.getItem(SELECTED_DOMAIN_KEY) ?? "";
  } catch {
    return "";
  }
}

function writeSelectedDomain(domain: string) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(SELECTED_DOMAIN_KEY, domain);
  } catch {
    /* ignore quota / unavailable */
  }
}

export default function OnlinePlayers() {
  const api = useApi();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [domains, setDomains] = createStore<DomainSummary[]>([]);
  const [selected, setSelected] = createSignal<string>(readSelectedDomain());
  const [snapshot, setSnapshot] = createSignal<DomainSnapshot | null>(null);
  const [connected, setConnected] = createSignal(false);
  const [loadingDomains, setLoadingDomains] = createSignal(true);
  const [now, setNow] = createSignal(Date.now());
  const [search, setSearch] = createSignal("");
  let domainSelect: HTMLSelectElement | undefined;

  const openHistory = (playerName: string) => {
    const d = selected();
    if (!d) return;
    navigate(`/online-players/${encodeURIComponent(d)}/${encodeURIComponent(playerName)}`);
  };

  const fetchDomains = async () => {
    const res = await api.onlinePlayers.getDomains();
    if (isOk(res.data)) {
      const list = (res.data.value.domains ?? []) as DomainSummary[];
      setDomains(reconcile(list, { key: "domain" }));
      if (list.length > 0 && !list.some(d => d.domain === selected())) setSelected(list[0].domain);
    }
    setLoadingDomains(false);
  };

  onMount(() => {
    void fetchDomains();
    const handle = window.setInterval(() => {
      if (document.visibilityState === "visible") void fetchDomains();
    }, DOMAINS_POLL_MS);
    const clock = window.setInterval(() => setNow(Date.now()), 1000);
    onCleanup(() => {
      clearInterval(handle);
      clearInterval(clock);
    });
  });

  createEffect(() => {
    const d = selected();
    if (d) writeSelectedDomain(d);
  });

  // The options render after the select's value binding, so restore the picked domain once they exist
  createEffect(() => {
    domains.length;
    const d = selected();
    if (domainSelect && domainSelect.value !== d) domainSelect.value = d;
  });

  createEffect(() => {
    const d = selected();
    if (!d) return;
    setSnapshot(null);
    setConnected(false);
    const unsub = subscribeDomainStream(d, setSnapshot, setConnected);
    onCleanup(unsub);
  });

  const filteredPlayers = createMemo(() => {
    const snap = snapshot();
    if (!snap) return [];
    const q = search().trim().toLowerCase();
    if (!q) return snap.players;
    return snap.players.filter(p => p.name.toLowerCase().includes(q));
  });

  return (
    <div class={styles.container}>
      <div class={styles.headerRow}>
        <h1><Text val="ONLINE_PLAYERS_TITLE" /></h1>
        <span class={styles.statusBadge} classList={{
          [styles.statusOnline]: connected(),
          [styles.statusOffline]: !connected(),
        }}>
          {connected() ? t("ONLINE_PLAYERS_LIVE") : t("ONLINE_PLAYERS_OFFLINE")}
        </span>
      </div>

      <div class={styles.toolbar}>
        <label class={styles.selectLabel}>
          <span><Text val="ONLINE_PLAYERS_DOMAIN" /></span>
          <select
            ref={domainSelect}
            class={styles.select}
            value={selected()}
            onChange={(e) => setSelected(e.currentTarget.value)}
            disabled={domains.length === 0}
          >
            <Show when={domains.length === 0}>
              <option value=""><Text val="ONLINE_PLAYERS_NO_DOMAINS" /></option>
            </Show>
            <For each={domains}>
              {(d) => (
                <option value={d.domain}>
                  {d.domain} ({d.onlineCount}/{d.totalKnownCount})
                </option>
              )}
            </For>
          </select>
        </label>
        <label class={`${styles.selectLabel} ${styles.searchLabel}`}>
          <span><Text val="ONLINE_PLAYERS_SEARCH" /></span>
          <input
            type="text"
            class={styles.searchInput}
            placeholder={t("ONLINE_PLAYERS_SEARCH_PLACEHOLDER")}
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
          />
        </label>
        <button
          type="button"
          class="btn btn-secondary btn-sm"
          onClick={() => void fetchDomains()}
          title={t("ONLINE_PLAYERS_REFRESH")}
        >
          <i class="fa-solid fa-rotate" />
        </button>
      </div>

      <div class="fcard v" style={{ gap: "var(--spacing-md)" }}>
        <Show when={!loadingDomains()} fallback={
          <div class={styles.skeleton}>
            <div class={styles.skeletonBar} />
            <div class={styles.skeletonBar} />
            <div class={styles.skeletonBar} />
          </div>
        }>
          <Show when={selected() && snapshot()} fallback={
            <div class={styles.empty}>
              <i class="fa-solid fa-network-wired" />
              <p><Text val="ONLINE_PLAYERS_SELECT_PROMPT" /></p>
            </div>
          }>
            <OnlinePlayersList
              players={filteredPlayers()}
              now={now()}
              onShowHistory={openHistory}
            />
          </Show>
        </Show>
      </div>
    </div>
  );
}
